import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Form, Input, Row, Col, Space, Select, App } from "antd";

import {
  departmentType,
  departmentTypeLabels,
  userTaxCategory,
  userTaxCategoryLabels,
} from "../../../constant/enum";
import { getBranchList } from "../../../general/api/branch.api";
import { getUser } from "../../../services/auth";
import { createUser, editUser } from "../api/userAPI";
import { IUserRequest, UserRequestSchema } from "../type/user";

interface UserFormProps {
  initialData?: IUserRequest;
  mode?: "create" | "update";
  userId?: string;
}

export const UserForm = ({
  initialData,
  mode = "create",
  userId,
}: UserFormProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message: messageApi } = App.useApp();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const user = getUser();
  const userRole = user.role;

  const { data: branchData } = useQuery({
    queryKey: ["branches"],
    queryFn: getBranchList,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const branchOptions =
    branchData?.results.map((branch: { id: string; branchName: string }) => ({
      value: branch.id,
      label: branch.branchName,
    })) || [];

  const departmentOptions = departmentType.map((department) => ({
    value: department,
    label: departmentTypeLabels[department],
  }));

  // Get the initial branch ID from either structure
  const initialBranchId = initialData?.branchId;

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
  } = useForm<IUserRequest>({
    resolver: zodResolver(UserRequestSchema),
    defaultValues: {
      isEmailVerified: true,
      ...initialData,
      branchId: initialBranchId,
      password: mode === "update" ? undefined : initialData?.password,
    },
  });

  const onSubmit = async (data: IUserRequest) => {
    setIsSubmitting(true);
    try {
      const submitData = { ...data };
      delete submitData.branchName;

      if (mode === "create") {
        await createUser(submitData);
        messageApi.success("Successfully user created!");
      } else {
        if (userId) {
          await editUser(userId, submitData);
          messageApi.success("Successfully user updated!");

          // Refresh data
          queryClient.invalidateQueries({ queryKey: ["user", userId] });
        }
      }
      navigate(-1);
    } catch (error: any) {
      messageApi.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const role = useWatch({
    control,
    name: "role",
  });

  const branchName = useWatch({
    control,
    name: "branchName",
  });

  const isBranchShown = useMemo(() => {
    return role !== "client" && role !== undefined && role !== "admin";
  }, [role]);

  const isSerembanBranchSelected = useMemo(() => {
    return branchName === "Seremban" || initialData?.branchName === "Seremban";
  }, [branchName, initialData?.branchName]);

  return (
    <Form
      layout="vertical"
      onFinish={handleSubmit(onSubmit, () => {
        messageApi.error("Please check all required fields");
      })}
    >
      <Row gutter={36}>
        {/* ================ Role Field ================ */}
        {userRole === "admin" && (
          <Col xs={24} md={12}>
            <Form.Item
              label="Role"
              name="role"
              hasFeedback
              validateStatus={errors.role ? "error" : ""}
              help={errors.role?.message}
              required
            >
              <Controller
                name="role"
                control={control}
                render={({ field }) => (
                  <Select
                    placeholder="Select Role"
                    {...field}
                    size="large"
                    options={[
                      { value: "admin", label: "Admin" },
                      { value: "branchAdmin", label: "Branch Admin" },
                      { value: "lawyer", label: "Lawyer" },
                      { value: "staff", label: "Staff" },
                      { value: "client", label: "Client" },
                      { value: "agent", label: "Agent" },
                    ]}
                    defaultValue="client"
                    allowClear
                  />
                )}
              />
            </Form.Item>
          </Col>
        )}

        {/* ================ Branch Field ================ */}
        {isBranchShown && (
          <>
            <Col xs={24} md={12}>
              <Form.Item
                label="Branch"
                name="branchId"
                hasFeedback
                validateStatus={errors.branchId ? "error" : ""}
                help={errors.branchId?.message}
                required={role !== "client" && role !== "admin"}
              >
                <Controller
                  name="branchId"
                  control={control}
                  defaultValue={initialBranchId}
                  render={({ field }) => {
                    return (
                      <Select
                        {...field}
                        placeholder="Select Branch"
                        size="large"
                        options={branchOptions}
                        allowClear
                        disabled={mode === "update" && role !== "admin"}
                        onChange={(value) => {
                          field.onChange(value);
                          // Set branchName for validation
                          const selectedBranch = branchData?.results.find(
                            (b: { id: string; branchName: string }) =>
                              b.id === value
                          );
                          setValue(
                            "branchName",
                            selectedBranch?.branchName || ""
                          );
                        }}
                      />
                    );
                  }}
                />
              </Form.Item>
            </Col>

            {role === "agent" && (
              <Col xs={24} md={12}>
                <Form.Item
                  label="Agency Name"
                  name="agency"
                  hasFeedback
                  validateStatus={errors.agency ? "error" : ""}
                  help={errors.agency?.message}
                  required
                >
                  <Controller
                    name="agency"
                    control={control}
                    render={({ field }) => (
                      <Input
                        placeholder="Please enter your agency name"
                        {...field}
                        size="large"
                      />
                    )}
                  />
                </Form.Item>
              </Col>
            )}
          </>
        )}

        {/* ================ Name Field ================ */}
        <Col xs={24} md={12}>
          <Form.Item
            label="Full Name"
            name="name"
            hasFeedback
            validateStatus={errors.name ? "error" : ""}
            help={errors.name?.message}
            required
          >
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Input
                  placeholder="Please enter your name"
                  {...field}
                  size="large"
                />
              )}
            />
          </Form.Item>
        </Col>

        {/* ================ IC Number Field ================ */}
        <Col xs={24} md={12}>
          <Form.Item
            label="IC Number"
            name="icNumber"
            hasFeedback
            validateStatus={errors.icNumber ? "error" : ""}
            help={errors.icNumber?.message}
            tooltip="Example: 900101012345"
            required
          >
            <Controller
              name="icNumber"
              control={control}
              render={({ field }) => (
                <Input
                  placeholder="Please enter your IC number"
                  {...field}
                  size="large"
                />
              )}
            />
          </Form.Item>
        </Col>

        {/* ================ Address Field ================ */}
        {/* <Col xs={24} md={12}>
          <Form.Item
            label="Address"
            name="address"
            hasFeedback
            validateStatus={errors.address ? "error" : ""}
            help={errors.address?.message}
          >
            <Controller
              name="address"
              control={control}
              render={({ field }) => (
                <Input
                  placeholder="Please enter your address"
                  {...field}
                  size="large"
                />
              )}
            />
          </Form.Item>
        </Col> */}

        {/* ================ Phone Field ================ */}
        <Col xs={24} md={12}>
          <Form.Item
            label="Phone Number"
            name="phoneNumber"
            hasFeedback
            validateStatus={errors.phoneNumber ? "error" : ""}
            help={errors.phoneNumber?.message}
            required
          >
            <Controller
              name="phoneNumber"
              control={control}
              render={({ field }) => (
                <Input
                  placeholder="Please enter your phone number"
                  {...field}
                  size="large"
                />
              )}
            />
          </Form.Item>
        </Col>

        {/* ================ Email Field ================ */}
        <Col xs={24} md={12}>
          <Form.Item
            label="Email"
            name="email"
            hasFeedback
            validateStatus={errors.email ? "error" : ""}
            help={errors.email?.message}
            required
          >
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <Input
                  placeholder="Please enter your email"
                  {...field}
                  size="large"
                />
              )}
            />
          </Form.Item>
        </Col>

        {/* ================ Password Field ================ */}
        <Col xs={24} md={12}>
          <Form.Item
            label="Password"
            name="password"
            hasFeedback
            validateStatus={errors.password ? "error" : ""}
            help={errors.password?.message}
            required={mode === "create"}
            tooltip={
              mode === "update"
                ? "Leave empty to keep current password"
                : undefined
            }
          >
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <Input.Password
                  placeholder={
                    mode === "create"
                      ? "Please enter your password"
                      : "Leave empty to keep current password"
                  }
                  {...field}
                  size="large"
                />
              )}
            />
          </Form.Item>
        </Col>

        {isSerembanBranchSelected && (
          <>
            {/* ================ Bank Account Number Field ================ */}
            <Col xs={24} md={12}>
              <Form.Item
                label="Bank Account Number"
                name="bankAccountNumber"
                hasFeedback
                validateStatus={errors.bankAccountNumber ? "error" : ""}
                help={errors.bankAccountNumber?.message}
                required={role === "staff" || role === "lawyer"}
              >
                <Controller
                  name="bankAccountNumber"
                  control={control}
                  render={({ field }) => (
                    <Input
                      placeholder="Please enter your bank account number"
                      {...field}
                      size="large"
                    />
                  )}
                />
              </Form.Item>
            </Col>

            {/* ================ Bank Account Name Field ================ */}
            <Col xs={24} md={12}>
              <Form.Item
                label="Bank Account Name"
                name="bankAccountName"
                hasFeedback
                validateStatus={errors.bankAccountName ? "error" : ""}
                help={errors.bankAccountName?.message}
                required={role === "staff" || role === "lawyer"}
              >
                <Controller
                  name="bankAccountName"
                  control={control}
                  render={({ field }) => (
                    <Input
                      placeholder="Please enter your bank account name"
                      {...field}
                      size="large"
                    />
                  )}
                />
              </Form.Item>
            </Col>

            {/* ================ KSWP Number Field ================ */}
            <Col xs={24} md={12}>
              <Form.Item
                label="KWSP Number"
                name="kswpNumber"
                hasFeedback
                validateStatus={errors.kswpNumber ? "error" : ""}
                help={errors.kswpNumber?.message}
                required={role === "staff" || role === "lawyer"}
              >
                <Controller
                  name="kswpNumber"
                  control={control}
                  render={({ field }) => (
                    <Input
                      placeholder="Please enter your KWSP number"
                      {...field}
                      size="large"
                    />
                  )}
                />
              </Form.Item>
            </Col>

            {/* ================ Position Field ================ */}
            <Col xs={24} md={12}>
              <Form.Item
                label="Position"
                name="position"
                hasFeedback
                validateStatus={errors.position ? "error" : ""}
                help={errors.position?.message}
                required={role === "staff" || role === "lawyer"}
              >
                <Controller
                  name="position"
                  control={control}
                  render={({ field }) => (
                    <Input
                      placeholder="Please enter your position"
                      {...field}
                      size="large"
                    />
                  )}
                />
              </Form.Item>
            </Col>

            {/* ================ Department Field ================ */}
            <Col xs={24} md={12}>
              <Form.Item
                label="Department"
                name="department"
                hasFeedback
                validateStatus={errors.department ? "error" : ""}
                help={errors.department?.message}
                required={role === "staff" || role === "lawyer"}
              >
                <Controller
                  name="department"
                  control={control}
                  render={({ field }) => (
                    <Select
                      placeholder="Select Department"
                      {...field}
                      size="large"
                      onChange={(value) => {
                        field.onChange(value);
                      }}
                      options={departmentOptions}
                      allowClear
                    />
                  )}
                />
              </Form.Item>
            </Col>

            {/* ================ Employee Number Field ================ */}
            <Col xs={24} md={12}>
              <Form.Item
                label="Employee Number"
                name="employeeNumber"
                hasFeedback
                validateStatus={errors.employeeNumber ? "error" : ""}
                help={errors.employeeNumber?.message}
                required={role === "staff" || role === "lawyer"}
              >
                <Controller
                  name="employeeNumber"
                  control={control}
                  render={({ field }) => (
                    <Input
                      placeholder="Please enter your employee number"
                      {...field}
                      size="large"
                    />
                  )}
                />
              </Form.Item>
            </Col>

            {/* ================ Tax Category Field ================ */}
            <Col xs={24} md={12}>
              <Form.Item
                label="Tax Category"
                name="taxCategory"
                hasFeedback
                validateStatus={errors.taxCategory ? "error" : ""}
                help={errors.taxCategory?.message}
                required={role === "staff" || role === "lawyer"}
              >
                <Controller
                  name="taxCategory"
                  control={control}
                  render={({ field }) => (
                    <Select
                      placeholder="Select Tax Category"
                      {...field}
                      size="large"
                      options={userTaxCategory.map((category) => ({
                        value: category,
                        label: userTaxCategoryLabels[category],
                      }))}
                      allowClear
                    />
                  )}
                />
              </Form.Item>
            </Col>
          </>
        )}
      </Row>

      {/* ================ Form Actions ================ */}
      <Form.Item>
        <Row justify="end">
          <Space
            direction="horizontal"
            wrap
            style={{ display: "flex", justifyContent: "flex-end" }}
          >
            <Button
              style={{ margin: 5 }}
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              style={{ margin: 5 }}
              type="primary"
              htmlType="submit"
              loading={isSubmitting}
            >
              {mode === "create" ? "Create User" : "Update User"}
            </Button>
          </Space>
        </Row>
      </Form.Item>
    </Form>
  );
};
